/**
 * API Service for Lumen POC
 *
 * This service handles communication with n8n workflow endpoints.
 * For data access (Firestore operations), use the firestore-proxy service instead.
 */

import { auth } from './firebase'

// Data Access Service URL (proxies to n8n for pipeline execution)
const DATA_ACCESS_URL = 'https://data-access-service.lumenframework.com'

const API_CONFIG = {
  baseUrl: DATA_ACCESS_URL,
  endpoints: {
    // Pipeline execution via Data Access Service (proxies to n8n)
    pipeline: '/pipeline/execute',
    batchRun: '/pipeline/batch',
    // Metrics via Data Access Service
    metrics: '/metrics/summary',
  },
  timeout: {
    default: 120000,   // 2 minutes
    pipeline: 600000,  // 10 minutes - n8n workflows can take a while
    batch: 300000,
  },
  retry: {
    maxAttempts: 3,
    baseDelay: 1000,
    maxDelay: 10000,
  },
}

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
}

async function getAuthToken(): Promise<string | null> {
  const user = auth.currentUser
  if (!user) return null
  return user.getIdToken()
}

async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  timeout: number = API_CONFIG.timeout.default,
  retryCount = 0
): Promise<ApiResponse<T>> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const token = await getAuthToken()
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    }

    const response = await fetch(`${API_CONFIG.baseUrl}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`)
    }

    const data = await response.json()
    return { success: true, data }
  } catch (error) {
    clearTimeout(timeoutId)

    // Retry logic for network errors
    if (retryCount < API_CONFIG.retry.maxAttempts && !(error instanceof DOMException && error.name === 'AbortError')) {
      const delay = Math.min(
        API_CONFIG.retry.baseDelay * Math.pow(2, retryCount),
        API_CONFIG.retry.maxDelay
      )
      await sleep(delay)
      return apiRequest<T>(endpoint, options, timeout, retryCount + 1)
    }

    const message = error instanceof Error ? error.message : 'Unknown error'
    return { success: false, error: message }
  }
}

// ============================================
// Pipeline Execution Types
// ============================================

export interface TestCase {
  case_id: string
  profile: {
    profile_id: string
    role: string
    tech_proficiency: string
    accessibility_needs: string
    language_level: string
  }
  task_type: string
  process_phase: string
  process_data: {
    process_number: string
    phase: string
    last_movement: string
  }
}

export interface ExecutionResult {
  execution_id: string
  case_id: string
  status: 'success' | 'failed' | 'error'
  metrics?: {
    tads: number
    retry_count: number
    flesch_index: number
    legal_term_ratio: number
    information_density: number
    component_count: number
    execution_time_ms: number
  }
  error_message?: string
}

export interface MetricsSummary {
  total_executions: number
  success_rate: number
  avg_tads: number
  avg_execution_time: number
  by_persona: Record<string, {
    success_rate: number
    avg_tads: number
  }>
}

// ============================================
// Pipeline Execution Functions
// ============================================

/**
 * Execute a single test case through the Lumen pipeline
 * NOTE: This function does NOT retry to prevent multiple n8n executions
 * NOTE: features.json is ALWAYS used - no longer an option
 */
export async function executeTestCase(
  testCase: TestCase
): Promise<ApiResponse<ExecutionResult>> {
  // n8n expects test_case_id, map from case_id
  // Note: use_features was removed - features.json is ALWAYS used on backend
  const payload = {
    test_case_id: testCase.case_id,
    profile: testCase.profile,
    task_type: testCase.task_type,
    process_phase: testCase.process_phase,
    process_data: testCase.process_data,
  }

  // Use direct fetch without retry to prevent multiple n8n executions
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout.pipeline)

  try {
    const token = await getAuthToken()
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    }

    const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.pipeline}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      const errorText = await response.text()
      return { success: false, error: `HTTP ${response.status}: ${errorText}` }
    }

    const data = await response.json()
    return { success: true, data }
  } catch (error) {
    clearTimeout(timeoutId)
    const message = error instanceof Error ? error.message : 'Unknown error'
    return { success: false, error: message }
  }
}

/**
 * Execute a single test case - fire and forget (doesn't wait for completion)
 * Used for batch execution where we just want to trigger executions without waiting.
 */
export async function executeTestCaseAsync(testCase: TestCase): Promise<ApiResponse<{ triggered: boolean }>> {
  const payload = {
    test_case_id: testCase.case_id,
    profile: testCase.profile,
    task_type: testCase.task_type,
    process_phase: testCase.process_phase,
    process_data: testCase.process_data
  }

  try {
    const token = await getAuthToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // Use fetch directly with no-wait pattern (short timeout just to confirm request was sent)
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout just to send

    await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.pipeline}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal: controller.signal,
    })

    clearTimeout(timeoutId)
    return { success: true, data: { triggered: true } }
  } catch (error) {
    // If aborted due to timeout, that's fine - request was sent
    if (error instanceof Error && error.name === 'AbortError') {
      return { success: true, data: { triggered: true } }
    }
    console.error('Error triggering test case:', error)
    return { success: false, error: String(error) }
  }
}

/**
 * Execute multiple test cases in batch
 */
export async function executeBatch(testCaseIds: string[]): Promise<ApiResponse<ExecutionResult[]>> {
  return apiRequest<ExecutionResult[]>(
    API_CONFIG.endpoints.batchRun,
    {
      method: 'POST',
      body: JSON.stringify({ case_ids: testCaseIds }),
    },
    API_CONFIG.timeout.batch
  )
}

/**
 * Get aggregated metrics from n8n workflow
 * Note: For real-time metrics from Firestore, use getMetricsSummary from firestore-proxy
 */
export async function getAggregatedMetrics(): Promise<ApiResponse<MetricsSummary>> {
  return apiRequest<MetricsSummary>(API_CONFIG.endpoints.metrics)
}

// Legacy export for backward compatibility
export const getMetricsSummary = getAggregatedMetrics

// ============================================
// IQAI Evaluations Types
// ============================================

export interface IQAIScores {
  content_adequacy: number
  visual_adequacy: number
  technical_consistency: number
  adaptation_effectiveness: number
}

export interface IQAIEvaluation {
  evaluation_id: string
  interface_id: string
  evaluator_id: string
  evaluator_name: string
  scores: IQAIScores
  iqai_score: number
  comments?: string
  timestamp: string
}

export interface IQAISummary {
  total_evaluations: number
  interfaces_evaluated: number
  evaluators_count: number
  avg_iqai_score: number
  avg_content_adequacy: number
  avg_visual_adequacy: number
  avg_technical_consistency: number
  avg_adaptation_effectiveness: number
}

export interface CreateIQAIRequest {
  interface_id: string
  evaluator_id: string
  evaluator_name: string
  content_adequacy: number
  visual_adequacy: number
  technical_consistency: number
  adaptation_effectiveness: number
  comments?: string
}

// ============================================
// IQAI Evaluation Functions
// ============================================

/**
 * Get all IQAI evaluations with optional filters
 */
export async function getIQAIEvaluations(filters?: {
  interface_id?: string
  evaluator_id?: string
  limit?: number
}): Promise<ApiResponse<IQAIEvaluation[]>> {
  const params = new URLSearchParams()
  if (filters?.interface_id) params.append('interface_id', filters.interface_id)
  if (filters?.evaluator_id) params.append('evaluator_id', filters.evaluator_id)
  if (filters?.limit) params.append('limit', filters.limit.toString())

  const query = params.toString() ? `?${params.toString()}` : ''
  const result = await apiRequest<{ success: boolean; data: IQAIEvaluation[]; count: number }>(
    `/iqai/evaluations${query}`
  )

  if (result.success && result.data) {
    return { success: true, data: result.data.data }
  }
  return { success: false, error: result.error }
}

/**
 * Get a single IQAI evaluation by ID
 */
export async function getIQAIEvaluation(evaluationId: string): Promise<ApiResponse<IQAIEvaluation>> {
  const result = await apiRequest<{ success: boolean; data: IQAIEvaluation }>(
    `/iqai/evaluations/${evaluationId}`
  )

  if (result.success && result.data) {
    return { success: true, data: result.data.data }
  }
  return { success: false, error: result.error }
}

/**
 * Create a new IQAI evaluation
 */
export async function createIQAIEvaluation(
  evaluation: CreateIQAIRequest
): Promise<ApiResponse<IQAIEvaluation>> {
  const result = await apiRequest<{ success: boolean; data: IQAIEvaluation }>(
    '/iqai/evaluations',
    {
      method: 'POST',
      body: JSON.stringify(evaluation),
    }
  )

  if (result.success && result.data) {
    return { success: true, data: result.data.data }
  }
  return { success: false, error: result.error }
}

/**
 * Get IQAI summary statistics
 */
export async function getIQAISummary(): Promise<ApiResponse<IQAISummary>> {
  const result = await apiRequest<{ success: boolean; data: IQAISummary }>(
    '/iqai/summary'
  )

  if (result.success && result.data) {
    return { success: true, data: result.data.data }
  }
  return { success: false, error: result.error }
}

export { API_CONFIG }

